import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  decimal,
  varchar,
  jsonb,
  index,
  date,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Chat tables for messaging functionality
export const chatChannels = pgTable("chat_channels", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  type: text("type").notNull(), // "intra-agency", "client-broker", "direct"
  clientId: integer("client_id"), // references clients.id - For client-broker channels
  participantIds: text("participant_ids").array(), // For direct messages between specific users
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const chatMessages = pgTable("chat_messages", {
  id: serial("id").primaryKey(),
  channelId: integer("channel_id").notNull(), // references chatChannels.id
  senderId: varchar("sender_id").notNull(), // references users.id
  content: text("content").notNull(),
  messageType: text("message_type").notNull().default("text"), // "text", "file", "image"
  fileUrl: text("file_url"), // For file attachments
  fileName: text("file_name"),
  isEdited: boolean("is_edited").notNull().default(false),
  isDeleted: boolean("is_deleted").notNull().default(false),
  replyToId: integer("reply_to_id"), // references chatMessages.id
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const chatChannelMembers = pgTable("chat_channel_members", {
  id: serial("id").primaryKey(),
  channelId: integer("channel_id").notNull(), // references chatChannels.id
  userId: varchar("user_id").notNull(), // references users.id
  role: text("role").notNull().default("member"), // "admin", "member"
  joinedAt: timestamp("joined_at").defaultNow(),
  lastReadAt: timestamp("last_read_at"),
});

export const contactInteractions = pgTable("contact_interactions", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id"), // references clients.id
  prospectId: integer("prospect_id"), // references prospects.id
  employeeId: integer("employee_id"), // references employees.id (optional - for employee-scoped interactions)
  type: varchar("type", { length: 50 }).notNull(), // "email", "phone_call", "meeting", "demo", "proposal_sent", "follow_up"
  subject: varchar("subject", { length: 255 }).notNull(),
  notes: text("notes"),
  contactDate: timestamp("contact_date").notNull(),
  outcome: varchar("outcome", { length: 50 }), // "positive", "neutral", "negative", "no_response"
  nextFollowUp: timestamp("next_follow_up"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Emails table for persisted outbound emails (mock-sent)
export const emails = pgTable("emails", {
  id: serial("id").primaryKey(),
  from: varchar("from", { length: 255 }).notNull(),
  to: text("to").notNull(), // comma-separated list
  cc: text("cc"), // comma-separated list
  bcc: text("bcc"), // comma-separated list
  subject: varchar("subject", { length: 512 }).notNull(),
  body: text("body").notNull(),
  clientId: integer("client_id"), // references clients.id
  prospectId: integer("prospect_id"), // references prospects.id
  status: varchar("status", { length: 50 }).notNull().default("sent"), // sent, failed, queued
  providerMessageId: varchar("provider_message_id", { length: 255 }),
  error: text("error"),
  sentAt: timestamp("sent_at"),
  createdAt: timestamp("created_at").defaultNow(),
  // Bulk email sending tracking columns
  templateId: integer("template_id"), // references email_templates.id
  bulkSendId: varchar("bulk_send_id", { length: 255 }), // groups related bulk sends
  recipientType: varchar("recipient_type", { length: 50 }), // 'client' | 'prospect'
  recipientId: integer("recipient_id"), // client or prospect ID
  mergeFieldsUsed: jsonb("merge_fields_used"), // JSONB of substituted fields
  openedAt: timestamp("opened_at"),
  clickedAt: timestamp("clicked_at"),
  bouncedAt: timestamp("bounced_at"),
  deliveredAt: timestamp("delivered_at"),
  retryCount: integer("retry_count").default(0),
}, (table) => ({
  templateIdIdx: index("emails_template_id_idx").on(table.templateId),
  bulkSendIdIdx: index("emails_bulk_send_id_idx").on(table.bulkSendId),
  recipientIdx: index("emails_recipient_idx").on(table.recipientType, table.recipientId),
}));

// Insert schemas
export const insertChatChannelSchema = createInsertSchema(chatChannels);
export const insertChatMessageSchema = createInsertSchema(chatMessages);
export const insertChatChannelMemberSchema =
  createInsertSchema(chatChannelMembers);
const emptyToUndefined = (v: unknown) => (typeof v === "string" && v.trim() === "" ? undefined : v);

export const insertContactInteractionSchema = createInsertSchema(contactInteractions)
  .omit({ id: true, createdAt: true })
  .extend({
    // A logged contact must say what kind of touchpoint it was and what it was
    // about; a blank form must not become a history entry.
    type: z.string().trim().min(1, "Contact type is required"),
    subject: z.string().trim().min(1, "Subject is required"),
    notes: z.preprocess(emptyToUndefined, z.string().optional().nullable()),
    outcome: z.preprocess(emptyToUndefined, z.string().max(50).optional().nullable()),
    // Accept ISO strings or Date objects from client; coerce to Date
    contactDate: z.coerce.date(),
    nextFollowUp: z.preprocess(emptyToUndefined, z.coerce.date().optional().nullable()),
  });

/** Shape of the Log Contact forms (raw input strings). Same required fields as the insert schema. */
export const contactInteractionFormSchema = z
  .object({
    type: z.string().trim().min(1, "Select a contact type"),
    subject: z.string().trim().min(1, "Subject is required"),
    contactDate: z.string().min(1, "Contact date is required"),
    notes: z.string().optional(),
    outcome: z.string().optional(),
    nextFollowUp: z.string().optional(),
  })
  .passthrough();

export const insertEmailSchema = createInsertSchema(emails)
  .omit({ id: true, createdAt: true })
  .extend({
    sentAt: z.coerce.date().optional(),
  });

// Types
export type ChatChannel = typeof chatChannels.$inferSelect;
export type InsertChatChannel = typeof chatChannels.$inferInsert;
export type ChatMessage = typeof chatMessages.$inferSelect;
export type InsertChatMessage = typeof chatMessages.$inferInsert;
export type ChatChannelMember = typeof chatChannelMembers.$inferSelect;
export type InsertChatChannelMember = typeof chatChannelMembers.$inferInsert;
export type ContactInteraction = typeof contactInteractions.$inferSelect;
export type InsertContactInteraction = z.infer<
  typeof insertContactInteractionSchema
>;

export type Email = typeof emails.$inferSelect;
export type InsertEmail = z.infer<typeof insertEmailSchema>;

// SMS Messages table for persisted outbound SMS (mock-sent)
export const smsMessages = pgTable("sms_messages", {
  id: serial("id").primaryKey(),
  from: varchar("from", { length: 20 }).notNull(), // Phone number (E.164)
  to: varchar("to", { length: 20 }).notNull(), // Phone number (E.164)
  message: text("message").notNull(), // SMS content
  clientId: integer("client_id"), // references clients.id
  prospectId: integer("prospect_id"), // references prospects.id
  status: varchar("status", { length: 50 }).notNull().default("sent"), // sent, failed, queued, delivered
  providerMessageId: varchar("provider_message_id", { length: 255 }), // Twilio message SID
  error: text("error"),
  sentAt: timestamp("sent_at"),
  createdAt: timestamp("created_at").defaultNow(),
  // Bulk SMS sending tracking columns
  templateId: integer("template_id"), // references sms_templates.id
  bulkSendId: varchar("bulk_send_id", { length: 255 }), // groups related bulk sends
  recipientType: varchar("recipient_type", { length: 50 }), // 'client' | 'prospect'
  recipientId: integer("recipient_id"), // client or prospect ID
  mergeFieldsUsed: jsonb("merge_fields_used"), // JSONB of substituted fields
  deliveredAt: timestamp("delivered_at"),
  failedAt: timestamp("failed_at"),
  retryCount: integer("retry_count").default(0),
}, (table) => ({
  templateIdIdx: index("sms_messages_template_id_idx").on(table.templateId),
  bulkSendIdIdx: index("sms_messages_bulk_send_id_idx").on(table.bulkSendId),
  recipientIdx: index("sms_messages_recipient_idx").on(table.recipientType, table.recipientId),
  providerMessageIdIdx: index("sms_messages_provider_message_id_idx").on(table.providerMessageId),
}));

export const insertSmsMessageSchema = createInsertSchema(smsMessages)
  .omit({ id: true, createdAt: true })
  .extend({
    from: z.string().regex(/^\+[1-9]\d{1,14}$/, "From phone must be in E.164 format (+1234567890)"),
    to: z.string().regex(/^\+[1-9]\d{1,14}$/, "To phone must be in E.164 format (+1234567890)"),
    message: z.string().max(1600, "SMS message cannot exceed 1600 characters"),
    sentAt: z.coerce.date().optional(),
    deliveredAt: z.coerce.date().optional(),
    failedAt: z.coerce.date().optional(),
  });

export type SmsMessage = typeof smsMessages.$inferSelect;
export type InsertSmsMessage = z.infer<typeof insertSmsMessageSchema>;

// Agency Email Settings for SendGrid Subuser management
export const agencyEmailSettings = pgTable("agency_email_settings", {
  id: serial("id").primaryKey(),
  agencyId: varchar("agency_id", { length: 255 }).notNull().unique(),

  // SendGrid Subuser (Reseller Model)
  sendgridSubuserId: integer("sendgrid_subuser_id"),
  sendgridSubuserName: varchar("sendgrid_subuser_name", { length: 100 }),
  sendgridSubuserApiKey: text("sendgrid_subuser_api_key"), // Encrypted

  // Sending Configuration
  sendingSubdomain: varchar("sending_subdomain", { length: 100 }),
  fromEmail: varchar("from_email", { length: 255 }),
  fromName: varchar("from_name", { length: 255 }),
  replyToEmail: varchar("reply_to_email", { length: 255 }),

  // Status
  provisioningStatus: varchar("provisioning_status", { length: 50 }).default("pending"),
  provisioningError: text("provisioning_error"),
  isActive: boolean("is_active").default(false),

  // Usage Tracking (for billing)
  emailsSentThisMonth: integer("emails_sent_this_month").default(0),
  lastUsageSync: timestamp("last_usage_sync"),

  // Future: Custom Domain Support
  customSendgridApiKey: text("custom_sendgrid_api_key"), // Encrypted
  useCustomProvider: boolean("use_custom_provider").default(false),

  // Audit
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  agencyIdIdx: index("agency_email_settings_agency_id_idx").on(table.agencyId),
}));

export const insertAgencyEmailSettingsSchema = createInsertSchema(agencyEmailSettings)
  .omit({ id: true, createdAt: true, updatedAt: true });

export type AgencyEmailSettings = typeof agencyEmailSettings.$inferSelect;
export type InsertAgencyEmailSettings = z.infer<typeof insertAgencyEmailSettingsSchema>;
